// Organizasyon teklifi hesapları — saf fonksiyonlar (React/ağ bağımlılığı yok).
//
// Kalem: { name, description, quantity, unit, unitPrice, vatRate }   → satır toplamı = adet × birim fiyat
// Sıra:  ara toplam → indirim (teklif başına tek, pricing.js computeDiscount ile aynı kural)
//        → KDV → genel toplam → kapora / kalan
// KDV ORANI KALEM BAŞINA (ör. yemek %10, salon/hizmet %20); kalemde oran yoksa (eski kayıt) teklifin vatRate'i.
// Kalemler oranlarına göre gruplanır; indirim gruplara tutarları oranında dağıtılır (faturadaki gibi; kuruşa aşağı),
// kalan kuruşlar en büyük gruba yazılır → grupların matrahları toplamı = indirimli tutar, hiçbir matrah eksi olmaz.
// Dahil/hariç teklif başına seçilir:
//   'included' → girilen fiyatlar KDV dahil. Genel toplam = indirimli tutar; vatAmount = içindeki KDV (bilgi amaçlı).
//   'excluded' → fiyatlar KDV hariç. Her grubun KDV'si = matrah × oran; genel toplam = indirimli + KDV'ler.
// Kapora: genel toplam üzerinden yüzde (tam liraya yuvarlanır) ya da tutar (genel toplama kırpılır).
//
// ⚠ backend/server.js'teki computeEventTotalsServer AYNI kuralları uygular ve kayıtta toplamları
//   yeniden hesaplayıp karşılaştırır. Burayı değiştirirsen orayı da değiştir.

import { round2, computeDiscount, parsePriceInput } from './pricing';

export const VAT_MODES = ['included', 'excluded'];
export const DEFAULT_VAT_RATE = 20;
// Kalem satırındaki KDV seçenekleri (Türkiye: genel %20, yemek/konaklama %10, bazı ürünler %1, istisna %0)
export const VAT_RATE_OPTIONS = [20, 10, 1, 0];
export const DEPOSIT_TYPES = ['percentage', 'amount'];
export const EVENT_STATUSES = ['Onay Bekliyor', 'Onaylandı', 'Onaylanmadı'];

export const EVENT_TYPE_SUGGESTIONS = [
  'Nişan', 'Düğün', 'Kına Gecesi', 'Söz', 'Mezuniyet Gecesi', 'Doğum Günü', 'Sünnet Düğünü',
  'Toplantı', 'Seminer / Konferans', 'Eğitim', 'Lansman', 'Gala Yemeği', 'Kokteyl', 'Yılbaşı Partisi', 'Diğer',
];
export const UNIT_SUGGESTIONS = ['kişi', 'adet', 'saat', 'gün', 'paket', 'masa', 'porsiyon', 'set'];

// Sayısal alan: "1.500" → 1500, "2,5" → 2.5, 120 → 120. Geçersiz/boş → NaN.
export const toEventNumber = (value) => {
  if (value === '' || value === null || value === undefined) return NaN;
  return typeof value === 'number' ? value : parsePriceInput(String(value));
};

// Satır toplamı (kuruşa yuvarlı). Geçersiz adet/fiyat → 0.
export const eventLineTotal = (item) => {
  const q = toEventNumber(item?.quantity);
  const p = toEventNumber(item?.unitPrice);
  if (!Number.isFinite(q) || !Number.isFinite(p) || q < 0 || p < 0) return 0;
  return round2(q * p);
};

const clampRate = (v) => Math.min(Math.max(v, 0), 100);
// Kuruşa AŞAĞI yuvarlama (önce 4 haneye: kayan nokta gürültüsü bir kuruş kaybettirmesin) — server.js evFloor2 ile aynı
const floor2 = (n) => Math.floor(Math.round((Number(n) || 0) * 10000) / 100) / 100;

// Kalemin KDV oranı (0–100). Kalemde geçerli oran yoksa fallback (teklifin oranı / varsayılan).
export const itemVatRate = (item, fallback = DEFAULT_VAT_RATE) => {
  const own = toEventNumber(item?.vatRate);
  if (Number.isFinite(own)) return clampRate(own);
  return clampRate(Number(fallback) || 0);
};

export const computeEventTotals = ({
  items = [], discountType = null, discountValue = 0,
  vatMode = 'included', vatRate = DEFAULT_VAT_RATE,
  depositType = null, depositValue = 0,
} = {}) => {
  const list = items || [];
  const subTotal = round2(list.reduce((acc, it) => acc + eventLineTotal(it), 0));
  const { amount: discountAmount, total: afterDiscount } = computeDiscount({ subTotal, type: discountType, value: Number(discountValue) });

  // KDV oran grupları (artan oran sırası). Matrah = grubun indirimden payı düşülmüş tutarı.
  const fallbackRate = clampRate(Number(vatRate) || 0);
  const grossByRate = new Map();
  list.forEach((it) => {
    const r = itemVatRate(it, fallbackRate);
    grossByRate.set(r, (grossByRate.get(r) || 0) + eventLineTotal(it));
  });
  const rates = [...grossByRate.keys()].sort((a, b) => a - b);
  const grosses = rates.map((r) => round2(grossByRate.get(r)));
  // Kalan kuruşları alan grup: tutarı en büyük olan (eşitlikte yüksek oran). Diğerleri aşağı yuvarlandığı için matrah ≥ 0.
  let restIndex = 0;
  grosses.forEach((g, i) => { if (g >= grosses[restIndex]) restIndex = i; });
  let allocated = 0;
  grosses.forEach((g, i) => {
    if (i !== restIndex) allocated = round2(allocated + (discountAmount > 0 && subTotal > 0 ? floor2((g * afterDiscount) / subTotal) : g));
  });
  const vatBreakdown = rates.map((rate, i) => {
    const g = grosses[i];
    const base = i === restIndex
      ? round2(afterDiscount - allocated)
      : (discountAmount > 0 && subTotal > 0 ? floor2((g * afterDiscount) / subTotal) : g);
    const vat = vatMode === 'excluded'
      ? round2((base * rate) / 100)
      : (rate > 0 ? round2(base - base / (1 + rate / 100)) : 0);
    return { rate, base, vat };
  });
  const vatAmount = round2(vatBreakdown.reduce((acc, g) => acc + g.vat, 0));
  const total = vatMode === 'excluded' ? round2(afterDiscount + vatAmount) : afterDiscount;
  // Tek oranlı teklifte o oran; karma oranlıda null (kalem yoksa teklifin oranı)
  const rate = rates.length === 1 ? rates[0] : (rates.length === 0 ? fallbackRate : null);

  let depositAmount = 0;
  const dv = Number(depositValue);
  if (DEPOSIT_TYPES.includes(depositType) && Number.isFinite(dv) && dv > 0 && total > 0) {
    // %100 kapora toplamın tamamıdır (tam liraya yuvarlanınca birkaç kuruşluk "kalan" kalmasın)
    depositAmount = depositType === 'percentage'
      ? (dv >= 100 ? total : Math.min(Math.round((total * dv) / 100), total))
      : round2(Math.min(dv, total));
  }
  const balance = round2(total - depositAmount);

  return { subTotal, discountAmount, afterDiscount, vatRate: rate, vatBreakdown, vatAmount, total, depositAmount, balance };
};

// Ekranda/PDF'te gösterilecek KDV satırları: %0 ve matrahı 0 olan gruplar hariç
export const visibleVatGroups = (totals) => (totals?.vatBreakdown || []).filter((g) => g.rate > 0 && g.base !== 0);
// Tutarı olan %0 KDV'li kalem var mı? ("Fiyatlara %20 KDV dahildir" notu bu kalemleri kapsamasın)
export const hasZeroVatItems = (totals) => (totals?.vatBreakdown || []).some((g) => g.rate === 0 && g.base > 0);
// "%10 ve %20" / "%20"
export const vatRatesText = (groups, fmt = (n) => String(n)) => {
  const parts = groups.map((g) => `%${fmt(g.rate)}`);
  return parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} ve ${parts[parts.length - 1]}`;
};

// Kayıtlı teklifin (API'den: DECIMAL'ler string) toplamlarını kalemlerinden yeniden kurar.
export const eventOfferTotals = (offer) => computeEventTotals({
  items: Array.isArray(offer?.items) ? offer.items : [],
  discountType: offer?.discountType,
  discountValue: offer?.discountValue,
  vatMode: offer?.vatMode,
  vatRate: offer?.vatRate ?? DEFAULT_VAT_RATE,
  depositType: offer?.depositType,
  depositValue: offer?.depositValue,
});

// Teklif süresi doldu mu? (yalnızca onay bekleyen teklifler için anlamlı)
export const isEventOfferExpired = (offer, today = new Date()) => {
  if (!offer?.validUntil || offer.status !== 'Onay Bekliyor') return false;
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const [y, m, d] = String(offer.validUntil).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return false;
  return new Date(y, m - 1, d) < t;
};

// 'YYYY-MM-DD' + n gün (yerel tarih; form varsayılanı için)
export const addDaysLocal = (dateStr, n) => {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

export const todayLocal = () => {
  const dt = new Date();
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
